import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { toWebpImageUrl } from '../utils/imageUrl';
import { getProductDisplayName } from '../utils/productDisplay';

/**
 * Hook to fetch products from Supabase products table
 * Matches the React Native ShopScreen implementation
 */
export const useProducts = (categoryFilter = 'all') => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query - match React Native implementation exactly
        // Use select('*') to get ALL fields like React Native does
        let query = supabase
          .from('products')
          .select('*')
          .eq('in_stock', true)
          .order('featured', { ascending: false })
          .order('created_at', { ascending: false });

        // Apply category filter if not 'all'
        if (categoryFilter && categoryFilter !== 'all') {
          query = query.eq('category', categoryFilter);
        }

        const { data: productsData, error: productsError } = await query;

        if (productsError) {
          throw productsError;
        }

        // Transform products - match React Native: keep all fields, ensure description is passed
        const transformedProducts = (productsData || []).map(product => {
          // Log to debug (only in development)
          if (process.env.NODE_ENV === 'development') {
            console.log('Product from DB (matching React Native):', {
              id: product.id,
              name: product.name,
              description: product.description,
              hasDescription: !!product.description
            });
          }

          // Spread all fields first (like React Native), then override specific ones
          return {
            ...product, // Keep ALL fields from database
            name: getProductDisplayName(product.name),
            // Ensure these specific fields are set correctly
            description: product.description || '', // Keep description as-is from DB
            price: product.price || 0,
            image_url: toWebpImageUrl(product.image_url),
            image: toWebpImageUrl(product.image_url), // For compatibility
            category: product.category || 'sensor',
            sensor_type: product.sensor_type,
            model: product.model,
            specifications: product.specifications || {},
            featured: product.featured || false,
            in_stock: product.in_stock !== false, // Default to true if not specified
            inStock: product.in_stock !== false, // For compatibility
            // Parse specifications if it's a JSON string
            features: product.specifications 
              ? (typeof product.specifications === 'string' 
                  ? JSON.parse(product.specifications) 
                  : product.specifications)
              : [],
          };
        });

        setProducts(transformedProducts);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message || 'Failed to fetch products');
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();

    // Set up real-time subscription for products
    const subscription = supabase
      .channel('products')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products'
        },
        () => {
          fetchProducts();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [categoryFilter]);

  return {
    products,
    isLoading,
    error,
    refetch: () => {
      setIsLoading(true);
    }
  };
};

/**
 * Hook to fetch a single product by ID
 */
export const useProduct = (productId) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productId) {
      setIsLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Match React Native: use select('*') to get ALL fields including description
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .single();

        if (productError) {
          throw productError;
        }

        if (productData) {
          // Match React Native: use product data directly, ensuring description is passed through
          // Log to debug (only in development)
          if (process.env.NODE_ENV === 'development') {
            console.log('Product detail from DB (matching React Native):', {
              id: productData.id,
              name: productData.name,
              description: productData.description,
              hasDescription: !!productData.description,
              allFields: Object.keys(productData)
            });
          }

          // Transform product - keep ALL fields from database like React Native does
          const transformedProduct = {
            ...productData, // Spread all fields first
            name: getProductDisplayName(productData.name),
            // Ensure these specific fields are set correctly
            description: productData.description || '', // Keep description as-is from DB
            price: productData.price || 0,
            image_url: toWebpImageUrl(productData.image_url),
            image: toWebpImageUrl(productData.image_url), // For compatibility
            category: productData.category || 'sensor',
            sensor_type: productData.sensor_type,
            model: productData.model,
            specifications: productData.specifications || {},
            featured: productData.featured || false,
            in_stock: productData.in_stock !== false,
            inStock: productData.in_stock !== false,
            // Parse specifications if it's a JSON string
            features: productData.specifications 
              ? (typeof productData.specifications === 'string' 
                  ? JSON.parse(productData.specifications) 
                  : productData.specifications)
              : [],
          };

          setProduct(transformedProduct);
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message || 'Failed to fetch product');
        setProduct(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  return {
    product,
    isLoading,
    error
  };
};

